import type { Coordinates } from '@alltrails/types/AlgoliaResults';

// Web's one server-driven UI (PageUI) contract. Every feature adds its section types to this union rather than declaring
// its own; one section type maps to one component in PageUISectionList.

/** Fields every section can carry. The server doesn't send `sectionId` on every section yet (ex: `divider`). */
type PageUISectionBase = {
  /** Stable id for the section, e.g. `trail-result-10242362`. */
  sectionId?: string;
};

export type PageUILlmSummarySection = PageUISectionBase & {
  sectionType: 'llm-summary';
  /** Plain text, not HTML. */
  summary: string;
};

export type PageUILlmErrorSummarySection = PageUISectionBase & {
  sectionType: 'llm-error-summary';
  /** Plain text, not HTML. */
  summary: string;
};

export type PageUITrailResultSection = PageUISectionBase & {
  sectionType: 'trail-result';
  trailId: number;
  verifiedMapId: number;
  name: string;
  locationLabel: string;
  avgRating: number;
  /** On the 1/3/5/7 difficulty scale. */
  difficultyRating: number;
  /** Meters. */
  length: number;
  /** Algolia-style, with the `trail/` prefix, e.g. `trail/us/california/fort-funston-trail`. */
  slug: string;
  /** Trailhead pin. */
  _geoloc: Coordinates;
  numReviews?: number;
  isClosed?: boolean;
  durationMinutes?: number;
  profilePhoto?: { url: string };
};

export type PageUIReviewHighlight = {
  /** Review id. */
  id: number;
  /** HTML with `<em>` around matches. Sanitize before rendering. */
  textWithHighlighting: string;
};

export type PageUICommunityInsightSection = PageUISectionBase & {
  sectionType: 'community-insight';
  trailId?: number;
  reviewHighlight: PageUIReviewHighlight;
};

export type PageUIDividerSection = PageUISectionBase & {
  sectionType: 'divider';
};

export type PageUISection =
  PageUILlmSummarySection | PageUILlmErrorSummarySection | PageUITrailResultSection | PageUICommunityInsightSection | PageUIDividerSection;

export type PageUISectionType = PageUISection['sectionType'];

export type PageUIMeta = {
  title: string;
  /** Ignored by web, and not always sent. */
  activeExperiments?: unknown[];
};

/**
 * A server-driven page. `sections` is typed as the known union, but a response can contain section types web doesn't
 * know yet, so code reading it must tolerate those (PageUISectionList renders nothing for them).
 */
export type PageUI = {
  meta: PageUIMeta;
  sections: PageUISection[];
};

/**
 * The one place that decides a section's identity.
 */
export const sectionKey = (section: PageUISection, index: number) => {
  switch (section.sectionType) {
    case 'trail-result':
      return `${section.sectionType}-${section.trailId}`;
    case 'community-insight':
      return `${section.sectionType}-${section.reviewHighlight.id}`;
    default:
      return String(index);
  }
};

/**
 * React keys for a page's sections. The server shouldn't repeat a trail or review on one page, but if it does, the
 * repeat appends its index to prevent merging of nodes.
 */
export const sectionKeys = (sections: PageUISection[]) => {
  const keys = new Set<string>();

  return sections.map((section, index) => {
    const key = sectionKey(section, index);
    const uniqueKey = keys.has(key) ? `${key}-${index}` : key;

    keys.add(uniqueKey);

    return uniqueKey;
  });
};
